// Per-item notifications for calendar events, jobs and reminders. Pure and
// alias-free so `node --test` can load it. The app has its own copy with the
// same behaviour (src/components/Notifications/notificationSettings.ts).

export interface Notifications {
	email: boolean;
	push: boolean;
	toAssignees: boolean;
	toCreator: boolean;
	/** Minutes before the time, newest alert last. Empty means None. */
	offsets: number[];
}

export type NotificationKind = 'event' | 'job' | 'reminder';

/** What a saved item carries. Keys can be missing on older documents. */
export interface NotificationSource {
	notifications?: Partial<Notifications> | null;
	notificationOffsetMinutes?: number | null;
}

export const NOTIFICATION_OFFSETS = [0, 5, 15, 30, 60];

export const DEFAULT_NOTIFICATIONS: Notifications = {
	email: false,
	push: true,
	toAssignees: true,
	toCreator: false,
	offsets: [0],
};

/** Valid offsets, de-duped and sorted descending, as they're stored. */
export const normalizeOffsets = (offsets: readonly number[] = []): number[] =>
	[...new Set(offsets)]
		.filter((offset) => NOTIFICATION_OFFSETS.includes(offset))
		.sort((a, b) => b - a);

/** Jobs use the word "event" in all copy. */
const nounFor = (kind: NotificationKind) => (kind === 'reminder' ? 'reminder' : 'event');

// Reminders saved before `notifications` keep what they did: both channels, at
// their old offset. Explicit checks: null means no notification, so never `?? 0`.
const fromLegacyReminder = (offset: number | null | undefined): Notifications => ({
	...DEFAULT_NOTIFICATIONS,
	email: true,
	offsets: offset === null ? [] : normalizeOffsets([offset === undefined ? 0 : offset]),
});

export const readNotifications = (
	item: NotificationSource | null | undefined,
	kind: NotificationKind,
): Notifications => {
	const stored = item?.notifications;
	if (stored) {
		return {
			...DEFAULT_NOTIFICATIONS,
			...stored,
			offsets: normalizeOffsets(stored.offsets ?? DEFAULT_NOTIFICATIONS.offsets),
		};
	}
	if (kind === 'job') return { ...DEFAULT_NOTIFICATIONS, offsets: [] };
	if (kind === 'reminder') return fromLegacyReminder(item?.notificationOffsetMinutes);
	return { ...DEFAULT_NOTIFICATIONS, offsets: [...DEFAULT_NOTIFICATIONS.offsets] };
};

export const sameNotifications = (a: Notifications, b: Notifications) =>
	a.email === b.email &&
	a.push === b.push &&
	a.toAssignees === b.toAssignees &&
	a.toCreator === b.toCreator &&
	a.offsets.length === b.offsets.length &&
	a.offsets.every((offset, index) => offset === b.offsets[index]);

/** The draft when an edit changed it, otherwise undefined: an edit only sends a change. */
export const changedNotifications = (
	stored: NotificationSource | null | undefined,
	kind: NotificationKind,
	draft: NotificationSource['notifications'],
): Notifications | undefined => {
	if (!draft) return undefined;
	const next = readNotifications({ notifications: draft }, kind);
	return sameNotifications(next, readNotifications(stored, kind)) ? undefined : next;
};

/**
 * What an edit sends. Every copy of an item carries its settings for display,
 * so they go back only as a change made in the form: a stale tab can't
 * overwrite them. The legacy offset is never sent.
 */
export const withNotificationsChange = <T extends NotificationSource>(
	item: T,
	change?: NotificationSource['notifications'],
): Omit<T, 'notifications' | 'notificationOffsetMinutes'> & NotificationSource => {
	const { notifications: _shown, notificationOffsetMinutes: _legacy, ...fields } = item;
	return change ? { ...fields, notifications: change } : fields;
};

/** A new reminder starts from the device's remembered timing (null = None). */
export const newReminderNotifications = (stickyOffset: number | null): Notifications => ({
	...DEFAULT_NOTIFICATIONS,
	offsets: stickyOffset === null ? [] : normalizeOffsets([stickyOffset]),
});

/** The offset to remember as the default: a single time, null for None, undefined for several. */
export const singleOffset = (offsets: number[]): number | null | undefined => {
	if (offsets.length === 0) return null;
	return offsets.length === 1 ? offsets[0] : undefined;
};

/** Picking a time clears None; it's a multi-select, so a second tap takes it off. */
export const toggleOffset = (notifications: Notifications, offset: number): Notifications => ({
	...notifications,
	offsets: normalizeOffsets(
		notifications.offsets.includes(offset)
			? notifications.offsets.filter((current) => current !== offset)
			: [...notifications.offsets, offset],
	),
});

export const sendsNothing = ({ offsets, email, push, toAssignees, toCreator }: Notifications) =>
	offsets.length === 0 || (!email && !push) || (!toAssignees && !toCreator);

const shortOffset = (offset: number, noun: string) => {
	if (offset === 0) return `At time of ${noun}`;
	return offset === 60 ? '1 hour before' : `${offset} min before`;
};

/** The When checklist, in order. */
export const whenOptions = (kind: NotificationKind): { offset: number; label: string }[] =>
	NOTIFICATION_OFFSETS.map((offset) => ({
		offset,
		label:
			offset === 0
				? `At time of ${nounFor(kind)}`
				: offset === 60
					? '1 hour before'
					: `${offset} minutes before`,
	}));

export interface NotificationPeople {
	viewerId?: string;
	/** Assigned people still on the location; ids tell the viewer and the creator apart. */
	assignees: { _id: string; name: string }[];
	/** The linked department's name */
	department?: string;
	/** Unknown creators read as "the creator"; null means the item has none (older jobs) */
	creator?: { _id?: string; name?: string } | null;
}

/**
 * `names` holds the location's active members, the only people the server can
 * reach, so an assignee who has left drops out and the creator fallback applies.
 */
export const notificationPeople = ({
	names,
	viewerId,
	_assignees,
	department,
	creator,
}: {
	names: Map<string, string>;
	viewerId?: string;
	_assignees: (string | undefined)[];
	department?: string;
	/** Leave it out on a new item, which is the viewer's; null means nobody. */
	creator?: string | null;
}): NotificationPeople => {
	const _creator = creator === undefined ? viewerId : creator;
	return {
		viewerId,
		assignees: [...new Set(_assignees)].flatMap((_id) => {
			const name = _id && names.get(_id);
			return name ? [{ _id, name }] : [];
		}),
		department,
		creator: _creator ? { _id: _creator, name: names.get(_creator) } : null,
	};
};

const capitalize = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);

/** `A`, `A and B`, `A, B and 3 others` */
export const joinNames = (names: string[]) => {
	if (names.length <= 2) return names.join(' and ');
	const others = names.length - 2;
	return `${names[0]}, ${names[1]} and ${others} ${others === 1 ? 'other' : 'others'}`;
};

// The viewer comes first, then the department, then people in the order given
const orderedLabels = (
	{ viewerId }: NotificationPeople,
	department: string | undefined,
	people: { _id?: string; name: string }[],
) => {
	const seen = new Set<string>();
	const unique = people.filter(({ _id }) => {
		if (!_id) return true;
		if (seen.has(_id)) return false;
		seen.add(_id);
		return true;
	});
	const isViewer = (_id?: string) => Boolean(viewerId && _id === viewerId);
	return [
		...(unique.some(({ _id }) => isViewer(_id)) ? ['you'] : []),
		...(department ? [`everyone in ${department}`] : []),
		...unique.filter(({ _id }) => !isViewer(_id)).map(({ name }) => name),
	];
};

const creatorPerson = ({ creator }: NotificationPeople) => ({
	_id: creator?._id,
	name: creator?.name || 'the creator',
});

const hasAssigned = ({ assignees, department }: NotificationPeople) =>
	assignees.length > 0 || Boolean(department);

/** The Assigned switch's subtitle. */
export const assignedCaption = (people: NotificationPeople) => {
	if (hasAssigned(people)) {
		return capitalize(joinNames(orderedLabels(people, people.department, people.assignees)));
	}
	return people.creator ? 'No one assigned. The creator is notified instead.' : 'No one assigned.';
};

/** The Creator switch's subtitle. */
export const creatorCaption = (people: NotificationPeople) =>
	capitalize(joinNames(orderedLabels(people, undefined, [creatorPerson(people)])));

// Who hears about it, as the server resolves it: the assigned people, or the
// creator when nobody is assigned, plus the creator when that's switched on.
const recipientLabels = (notifications: Notifications, people: NotificationPeople) => {
	const assigned = notifications.toAssignees && hasAssigned(people);
	const addCreator = notifications.toCreator || (notifications.toAssignees && !assigned);
	return orderedLabels(people, assigned ? people.department : undefined, [
		...(assigned ? people.assignees : []),
		...(addCreator && people.creator ? [creatorPerson(people)] : []),
	]);
};

// Nobody is reached when nothing is set to go out, or there's nobody to send it to.
const reachedLabels = (notifications: Notifications, people: NotificationPeople) =>
	sendsNothing(notifications) ? [] : recipientLabels(notifications, people);

/** The value on the form's Notify row. */
export const notificationSummary = (
	notifications: Notifications,
	kind: NotificationKind,
	people: NotificationPeople,
) => {
	if (reachedLabels(notifications, people).length === 0) return 'None';
	const { offsets } = notifications;
	return offsets.length === 1 ? shortOffset(offsets[0], nounFor(kind)) : `${offsets.length} alerts`;
};

const channelText = ({ email, push }: Notifications) => {
	if (email && push) return 'an app notification and email';
	return push ? 'an app notification' : 'an email';
};

const joinList = (items: string[]) =>
	items.length > 1
		? `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`
		: items[0];

const timesText = (offsets: number[], noun: string) => {
	const before = offsets
		.filter((offset) => offset > 0)
		.map((offset) => (offset === 60 ? '1 hour before' : `${offset} minutes before`));
	return offsets.includes(0)
		? joinList([...before, `at the time of the ${noun}`])
		: `${joinList(before)} the ${noun}`;
};

/** The plain sentence under the panel. */
export const notificationSentence = ({
	notifications,
	kind,
	people,
	allDay = false,
}: {
	notifications: Notifications;
	kind: NotificationKind;
	people: NotificationPeople;
	allDay?: boolean;
}) => {
	const labels = reachedLabels(notifications, people);
	if (labels.length === 0) return 'No notifications will be sent.';
	const who = capitalize(joinNames(labels));
	const sentence = `${who} will get ${channelText(notifications)} ${timesText(
		notifications.offsets,
		nounFor(kind),
	)}.`;
	return allDay ? `${sentence} All-day events alert at 9:00 AM.` : sentence;
};
